import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { fetchAnnouncements, type AnnouncementItem } from "../api/announcements";
import { AtlasPanel } from "./AtlasPanel";
import { Markdown } from "./Markdown";

// Read-only archive of announcements on the profile page — everything the user
// has been shown (dismissed) plus anything still unread. Shares the
// ["announcements"] cache with AnnouncementOverlay, so dismissing the modal
// moves the item here without a refetch.

function AnnouncementRow({ item }: { item: AnnouncementItem }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "10px 0",
          textAlign: "left",
        }}
      >
        {expanded ? (
          <ChevronDown size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
        ) : (
          <ChevronRight size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
        )}
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-primary)",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.title}
        </span>
        {!item.dismissedAt && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--text-accent)",
              border: "1px solid var(--border-gold)",
              borderRadius: "var(--radius-pill)",
              padding: "1px 8px",
              flexShrink: 0,
            }}
          >
            Unread
          </span>
        )}
        <span style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0 }}>
          {new Date(item.createdAt).toLocaleDateString()}
        </span>
      </button>
      {expanded && (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--text-secondary)",
            margin: "0 0 12px",
            paddingLeft: 22,
          }}
        >
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt=""
              style={{
                width: "100%",
                maxWidth: 360,
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                marginBottom: 10,
                display: "block",
              }}
            />
          )}
          <Markdown text={item.body} />
          {item.ctaLabel && item.ctaUrl && (
            <a
              href={item.ctaUrl}
              {...(item.ctaUrl.startsWith("/")
                ? {}
                : { target: "_blank", rel: "noopener noreferrer" })}
              style={{ fontSize: 13, fontWeight: 600, color: "var(--text-accent)" }}
            >
              {item.ctaLabel} →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function AnnouncementsPanel() {
  const query = useQuery({
    queryKey: ["announcements"],
    queryFn: fetchAnnouncements,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const items = [...(query.data?.active ?? []), ...(query.data?.dismissed ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <AtlasPanel title="Announcements">
      {query.isLoading ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
          Nothing yet. Announcements from the Atlas team will appear here.
        </p>
      ) : (
        <div>
          {items.map((item) => (
            <AnnouncementRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </AtlasPanel>
  );
}
